import { Star } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useUpdateCustomer } from '@/hooks/useCustomers';

interface CustomerReviewRequestsCardProps {
  customer: { id: string; review_requests_opted_out?: boolean | null };
  canEdit: boolean;
}

/**
 * Per-customer opt-out from the automated review request. Honoured when the
 * request is queued and again when it is sent, so flipping it while one is
 * waiting still stops it.
 */
export function CustomerReviewRequestsCard({ customer, canEdit }: CustomerReviewRequestsCardProps) {
  const updateCustomer = useUpdateCustomer();
  const optedOut = customer.review_requests_opted_out === true;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Review requests</CardTitle>
        </div>
        <CardDescription>Automatic "how did we do?" message after an invoice is paid in full.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label htmlFor="cust-review-optout" className="font-medium">
              {optedOut ? 'Never ask this customer' : 'Ask this customer for reviews'}
            </Label>
            <p className="text-sm text-muted-foreground">
              {optedOut
                ? 'They will not receive review requests, including any already waiting.'
                : 'Turn off if they have asked not to be contacted or already left one.'}
            </p>
          </div>
          <Switch
            id="cust-review-optout"
            checked={!optedOut}
            onCheckedChange={checked => updateCustomer.mutate({ id: customer.id, review_requests_opted_out: !checked })}
            disabled={!canEdit || updateCustomer.isPending}
          />
        </div>
      </CardContent>
    </Card>
  );
}
